import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { GraduationCap, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrainingForm = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_email: "",
    contact_affiliation: "",
    training_needs: "",
    preferred_training: "",
    number_of_participants: "",
    tentative_dates: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("contact_name", formData.contact_name);
      formDataToSend.append("contact_email", formData.contact_email);
      if (formData.contact_affiliation) {
        formDataToSend.append("contact_affiliation", formData.contact_affiliation);
      }
      formDataToSend.append("training_needs", formData.training_needs);
      formDataToSend.append("preferred_training", formData.preferred_training);
      if (formData.number_of_participants) {
        formDataToSend.append("number_of_participants", formData.number_of_participants);
      }
      if (formData.tentative_dates) {
        formDataToSend.append("tentative_dates", formData.tentative_dates);
      }
      if (file) {
        formDataToSend.append("file", file);
      }

      await axios.post(`${API}/submissions/training`, formDataToSend, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Training request submitted successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center mb-4">
              <GraduationCap className="h-6 w-6 text-teal-600" />
            </div>
            <CardTitle className="text-2xl" >
              Research Training & Capacity Building
            </CardTitle>
            <CardDescription>
              Structured programmes to build strong methodological and analytical competence
            </CardDescription>
            <p className="text-sm text-gray-600 mt-2 italic">
              Examples: Scoping reviews, systematic reviews, meta-analysis, medical education workshops, 
              qualitative evidence synthesis, GRADE assessment for systematic reviews of interventions, etc.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information Section */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="contact_name">Your Name *</Label>
                    <Input
                      id="contact_name"
                      data-testid="contact-name-input"
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="Enter your full name"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_email">Email Address *</Label>
                    <Input
                      id="contact_email"
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="your.email@example.com"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_affiliation">Affiliation (Optional)</Label>
                    <Input
                      id="contact_affiliation"
                      data-testid="contact-affiliation-input"
                      value={formData.contact_affiliation}
                      onChange={(e) => setFormData({ ...formData, contact_affiliation: e.target.value })}
                      placeholder="University/Organization name"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>

              {/* Training Details Section */}
              <div>
                <Label htmlFor="training_needs">Training Needs *</Label>
                <Textarea
                  id="training_needs"
                  data-testid="training-needs-textarea"
                  required
                  value={formData.training_needs}
                  onChange={(e) => setFormData({ ...formData, training_needs: e.target.value })}
                  placeholder="Describe your research training needs in detail"
                  rows={5}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="preferred_training">Preferred Training Type *</Label>
                <Input
                  id="preferred_training"
                  data-testid="preferred-training-input"
                  required
                  value={formData.preferred_training}
                  onChange={(e) => setFormData({ ...formData, preferred_training: e.target.value })}
                  placeholder="e.g., Online course, Workshop, One-on-one mentoring"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="number_of_participants">Number of Participants Anticipated (Optional)</Label>
                <Input
                  id="number_of_participants"
                  data-testid="number-of-participants-input"
                  type="text"
                  value={formData.number_of_participants}
                  onChange={(e) => setFormData({ ...formData, number_of_participants: e.target.value })}
                  placeholder="e.g., 20-25 participants"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="tentative_dates">Tentative Date/s (Optional)</Label>
                <Input
                  id="tentative_dates"
                  data-testid="tentative-dates-input"
                  type="text"
                  value={formData.tentative_dates}
                  onChange={(e) => setFormData({ ...formData, tentative_dates: e.target.value })}
                  placeholder="e.g., March 2025 or 15-17 March 2025"
                  className="mt-2"
                />
              </div>

              {/* Disclaimer Section */}
              <div className="border-t pt-4">
                <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="disclaimer-checkbox"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    required
                  />
                  <label htmlFor="disclaimer-checkbox" className="text-sm text-gray-700">
                    I/we agree with the{" "}
                    <button
                      type="button"
                      onClick={() => setShowDisclaimerModal(true)}
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      disclaimer
                    </button>
                    {" "}by the EviDent Research Consultancy Ltd
                  </label>
                </div>
              </div>

              <div>
                <Label htmlFor="file">Attach Document (Optional)</Label>
                <Input
                  id="file"
                  data-testid="file-input"
                  type="file"
                  onChange={(e) => {
                    const selectedFile = e.target.files[0];
                    if (selectedFile) {
                      // Check file size (10 MB = 10 * 1024 * 1024 bytes)
                      if (selectedFile.size > 10 * 1024 * 1024) {
                        toast.error(`File too large! Maximum size is 10 MB. Your file is ${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`);
                        e.target.value = ''; // Clear the input
                        setFile(null);
                      } else {
                        setFile(selectedFile);
                      }
                    }
                  }}
                  className="mt-2"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: PDF, DOC, DOCX, TXT, PNG, JPG (Max 10MB)
                </p>
              </div>

              <div className="flex space-x-4">
                <Button
                  type="submit"
                  data-testid="submit-training-btn"
                  disabled={submitting || !disclaimerAccepted}
                  className="bg-teal-600 hover:bg-teal-700 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Disclaimer Modal */}
      <Dialog open={showDisclaimerModal} onOpenChange={setShowDisclaimerModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Research Advice Disclaimer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
              <p className="text-sm sm:text-base leading-relaxed text-gray-800">
                The information, guidance, and support provided by <strong>EVIDENT RESEARCH CONSULTANCY LTD</strong>, 
                including evidence synthesis, research methods consultancy, training, and related services, are intended 
                for general informational and educational purposes only. We do not provide legal, clinical, or medical 
                advice, and our input should not be relied upon as a substitute for professional judgement in those areas.
              </p>
            </div>
            
            <p className="text-sm sm:text-base leading-relaxed text-gray-700">
              All decisions based on the advice or materials we provide must be independently reviewed and validated by 
              the client. EVIDENT RESEARCH CONSULTANCY LTD accepts no liability for actions taken or not taken based on 
              the use of our research advice or outputs.
            </p>

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setShowDisclaimerModal(false)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingForm;